import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getPhaseConfig } from './PhaseChip';

export default function PhaseRadar({ scores, size = 200, variant = 'card' }) {
  const { t } = useTranslation();
  if (!scores) return null;

  const svgPadding = variant === 'plain' ? 22 : 20;
  const viewSize = size + svgPadding * 2;

  const phases = [
    { key: 'accumulation', angle: 0 },
    { key: 'markup', angle: 90 },
    { key: 'distribution', angle: 180 },
    { key: 'absorption', angle: 270 },
  ];

  const center = viewSize / 2;
  const maxR = size / 2 - 35;

  const toXY = (angle, r) => {
    const rad = (angle - 90) * (Math.PI / 180);
    return {
      x: center + r * Math.cos(rad),
      y: center + r * Math.sin(rad),
    };
  };

  // Build polygon points
  const points = phases.map((p) => {
    const val = scores[p.key] || 0;
    const { x, y } = toXY(p.angle, val * maxR);
    return `${x},${y}`;
  }).join(' ');

  // Winner
  const winner = phases.reduce((best, p) =>
    (scores[p.key] || 0) > (scores[best.key] || 0) ? p : best
  , phases[0]);
  const winnerConfig = getPhaseConfig(winner.key);

  return (
    <Box sx={{
      background: variant === 'card' ? '#fff' : 'transparent',
      borderRadius: '12px',
      border: variant === 'card' ? '1px solid #e2e8f0' : 'none',
      p: variant === 'card' ? 2.5 : 0,
      textAlign: 'center',
    }}>
      <Typography sx={{
        fontSize: '0.68rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#94a3b8',
        mb: 1.5,
      }}>
        {t('phase.radar.title')}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <svg width={size} height={size} viewBox={`0 0 ${viewSize} ${viewSize}`} style={{ display: 'block', margin: '0 auto' }}>
          {/* Background circle */}
          <circle cx={center} cy={center} r={maxR + 5}
            fill="#fafbfc" stroke="none"
          />

          {/* Grid circles */}
          {[0.25, 0.5, 0.75, 1.0].map((r) => (
            <circle key={r} cx={center} cy={center} r={r * maxR}
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="1"
              strokeDasharray={r < 1 ? '3,3' : 'none'}
            />
          ))}

          {/* Grid labels */}
          {[0.25, 0.5, 0.75, 1.0].map((r) => (
            <text key={r} x={center + 3} y={center - r * maxR + 3}
              fill="#cbd5e1" fontSize="8" fontWeight="600"
            >
              {Math.round(r * 100)}
            </text>
          ))}

          {/* Axis lines */}
          {phases.map((p) => {
            const { x, y } = toXY(p.angle, maxR);
            return (
              <line key={p.key} x1={center} y1={center} x2={x} y2={y}
                stroke="#e2e8f0" strokeWidth="1"
              />
            );
          })}

          {/* Filled polygon */}
          <polygon
            points={points}
            fill={`${winnerConfig.color}15`}
            stroke={winnerConfig.color}
            strokeWidth="2"
            strokeLinejoin="round"
          />

          {/* Dots + labels */}
          {phases.map((p) => {
            const val = scores[p.key] || 0;
            const config = getPhaseConfig(p.key);
            const dot = toXY(p.angle, val * maxR);
            const labelPos = toXY(p.angle, maxR + 18);

            return (
              <g key={p.key}>
                {/* Outer ring */}
                <circle cx={dot.x} cy={dot.y} r={6}
                  fill={`${config.color}20`} stroke={config.color} strokeWidth="1.5"
                />
                {/* Inner dot */}
                <circle cx={dot.x} cy={dot.y} r={3}
                  fill={config.color}
                />
                {/* Label */}
                <text
                  x={labelPos.x} y={labelPos.y}
                  textAnchor="middle" dominantBaseline="middle"
                  fill="#64748b" fontSize="9" fontWeight="700"
                >
                  {t(`phase.phases.${p.key}`)}
                </text>
                {/* Value */}
                <text
                  x={dot.x} y={dot.y - 12}
                  textAnchor="middle"
                  fill={config.color} fontSize="10" fontWeight="800"
                  fontFamily="monospace"
                >
                  {(val * 100).toFixed(0)}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
    </Box>
  );
}
